import { Dispatch, SetStateAction } from "react";
import { Box, Checkbox, FormControlLabel, InputAdornment, TextField } from "@mui/material";
import { ImperialMetricDisplayPreference } from "@WahooFitness/cloud-client-types";
import { t } from "@lingui/macro";
import UnitPreferenceToggle from "@/components/User/AboutYou/UnitPreferenceToggle";
import { Responses } from "@/components/Quiz/useQuiz";
import useNumericDistanceEntry from "./useNumericDistanceEntry";

interface Props {
  initialVal?: number | "skip";
  min: number; // in km
  max: number; // in km
  skipText?: string;
  userUnitPreference: ImperialMetricDisplayPreference;
  setUserUnitPreference: (unit: ImperialMetricDisplayPreference) => void;
  setCurrentStepHasValue: (val: boolean) => void;
  setCurrentStepValid: (val: boolean) => void;
  setResponses: Dispatch<SetStateAction<Responses | undefined>>;
  questionId: number;
}

function NumericDistanceEntry({
  initialVal,
  min,
  max,
  skipText,
  userUnitPreference,
  setUserUnitPreference,
  setCurrentStepHasValue,
  setCurrentStepValid,
  setResponses,
  questionId,
}: Props) {
  const { val, skipChecked, error, isImperial, onChange, handleUnitChange, handleSkipToggle } =
    useNumericDistanceEntry({
      initialVal,
      min,
      max,
      userUnitPreference,
      setUserUnitPreference,
      setCurrentStepHasValue,
      setCurrentStepValid,
      setResponses,
      questionId,
    });

  return (
    <Box display="flex" flexDirection="column" width="100%" gap={2}>
      <UnitPreferenceToggle
        value={isImperial ? "imperial" : "metric"}
        onChange={handleUnitChange}
        hideCustom
        hideUomText
        toggleGroupColor="info"
        showBorder={false}
      />
      <TextField
        fullWidth
        value={val}
        onChange={onChange}
        type="number"
        helperText={!skipChecked ? error : ""}
        error={!!error && !skipChecked}
        disabled={skipChecked}
        autoFocus
        slotProps={{
          htmlInput: { inputMode: "decimal", "data-testid": "quiz-distance-entry" },
          input: {
            endAdornment: (
              <InputAdornment position="end">{isImperial ? t`mi` : t`km`}</InputAdornment>
            ),
          },
        }}
      />
      {skipText && (
        <Box mx={2}>
          <FormControlLabel
            control={<Checkbox checked={skipChecked} onChange={handleSkipToggle} />}
            label={skipText}
          />
        </Box>
      )}
    </Box>
  );
}

export default NumericDistanceEntry;
